function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Card, Pill } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import { Modal } from "../audiences/ui";
import DataShell, { Btn, Notice, isAdmin } from "./shell";
import { hasTenantFeature } from "../../functions/tenant-features";
import { resolveBusinessModel } from "../sales/territory-common";
import { getDigestSettings, putDigestSettings, listDigestRuns, getDigestRun, sendDigestNow, listDigestOptOuts, addDigestOptOut, removeDigestOptOut, listReps, listStores, DIGEST_KINDS, DIGEST_SECTIONS, currentUserEmail, isSalesUser } from "../sales/sales-quotes-api";
import { toast } from "../audiences/api";
import { fmtDate, fmtDateTime, relTime, errText } from "../quotes/common";

/*
 * Manage Data › Sales digests (SALES_DIGESTS.md §9; layout per the 2026-10 Digests mockup).
 * Amber banner when reps lack emails, then one card per digest (Owner, Rep, Store): on/off switch,
 * a one-sentence summary in the tenant's local time ("Every Monday at 7:00 AM New York time ..."),
 * Send me a test and Change. Change opens the settings in an even 4-column grid: How often (switches
 * between the weekly and monthly edition rows), On, At, Time zone (shared by every digest), Who gets
 * it, Replies go to, Covering, Which orders count, What's in it (section pills), then last run,
 * Send me a test, Send it now and Save. Below: Sent (delivery history) and Unsubscribed (opt-outs).
 * Day and hour are stored in the tenant zone as before; nothing here converts times.
 *
 * API: GET/PUT /v1/sales/digests/settings · GET /v1/sales/digests/runs?kind=&top= ·
 * GET /v1/sales/digests/runs/{id} · POST /v1/sales/digests/{kind}/send-now.
 * DS 2026-10-05: digests run beside the Ambella Friday script with a per-tenant
 * run-now; the journey hook enrols dealers at `at_risk`.
 */
const DAYS = [["1", "Monday"], ["2", "Tuesday"], ["3", "Wednesday"], ["4", "Thursday"], ["5", "Friday"], ["6", "Saturday"], ["0", "Sunday"]];
const DAY_NAME = Object.fromEntries(DAYS);
const hourLabel = h => {
  const x = Number(h) || 0;
  return `${x % 12 || 12}:00 ${x < 12 ? "AM" : "PM"}`;
};
const HOURS = Array.from({
  length: 24
}, (_, h) => [String(h), hourLabel(h)]);
const ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "America/Toronto", "America/Vancouver", "Europe/London", "Europe/Paris", "Australia/Sydney"];
/** "America/New_York" → "New York time" (same wording as the report schedule sheet). The stored value stays the IANA zone. */
const tzCity = tz => !tz || tz === "UTC" || tz === "Etc/UTC" ? "Universal time" : `${String(tz).split("/").pop().replace(/_/g, " ")} time`;
const cap = t => t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
const listWords = a => a.length <= 1 ? a[0] || "" : a.length === 2 ? `${a[0]} and ${a[1]}` : a.length === 3 ? `${a[0]}, ${a[1]} and ${a[2]}` : `${a[0]}, ${a[1]} and ${a.length - 2} others`;
// Plain section names; anything new from the API falls back to DIGEST_SECTIONS' label.
const SECTION_LABELS = {
  owner: {
    pace: "Sales against plan",
    reps: "Reps",
    dealers: "Dealers",
    coverage: "Territory coverage",
    demand_gaps: "Demand gaps",
    desk: "Quotes and orders",
    marketing: "Marketing"
  },
  rep: {
    dealers: "Who to call",
    reorder: "Reorder suggestions",
    demand_gaps: "Demand gaps",
    territory: "Their territory",
    desk: "Open quotes and orders"
  },
  store: {
    categories: "Categories",
    customers: "Customers",
    reputation: "Reviews",
    web: "Online to in-store"
  }
};
// "Off · ..." sentence for a digest that is switched off: who it is for, then when it would go.
const KIND_ABOUT = {
  owner: "the whole business for owners and sales leaders,",
  rep: "each rep gets their own accounts",
  store: "each store manager gets their own store"
};
const KIND_ABOUT_TAIL = {
  rep: ": who to call, open quotes and their territory"
};
/** One plain line for a run: "sent to 2 of 2 people". */
const runWords = r => {
  const skipped = (r.skipped || []).length;
  if (r.status === "test") return "test copy";
  if (r.status === "running") return "sending now";
  if (r.status === "failed") return `didn't send${r.error ? ` (${r.error})` : ""}`;
  const base = `sent to ${r.sent} of ${r.recipients} ${r.recipients === 1 ? "person" : "people"}`;
  return [base, skipped ? `${skipped} skipped` : null, r.bandChanges ? `${r.bandChanges} dealer${r.bandChanges === 1 ? "" : "s"} changed risk level` : null].filter(Boolean).join(", ");
};
// Show the edition that is on: monthly when only the monthly one is switched on, weekly otherwise.
const initialCadence = d => {
  const out = {
    owner: "weekly",
    rep: "weekly",
    store: "weekly"
  };
  (d && d.settings || []).forEach(r => {
    if (r.cadence === "monthly" && r.enabled && !(d.settings || []).some(x => x.kind === r.kind && x.cadence === "weekly" && x.enabled)) out[r.kind] = "monthly";
  });
  return out;
};
const runTone = s => ({
  sent: "success",
  partial: "warning",
  failed: "danger",
  running: "info",
  test: "neutral"
})[s] || "neutral";
const emailOk = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

/** Email chip input: Enter, comma or blur adds a valid address to the list (max 10). Module-level so it keeps focus across renders. */
function ChipInput({
  list,
  onChange,
  placeholder
}) {
  const [v, setV] = React.useState("");
  const add = () => {
    const e = v.trim().toLowerCase();
    if (emailOk(e) && !list.includes(e) && list.length < 10) onChange(list.concat([e]));
    setV("");
  };
  return /*#__PURE__*/React.createElement("input", {
    className: "dd-chip-in",
    placeholder: placeholder || "Add an email",
    value: v,
    onChange: e => setV(e.target.value),
    onKeyDown: e => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        add();
      }
    },
    onBlur: add
  });
}
const RETAIL_HIDDEN_OWNER = ["reps", "dealers", "coverage", "desk"];
class SalesDigestsView extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "save", async () => {
      const {
        data
      } = this.state;
      const bad = data.settings.find(r => r.enabled && r.kind === "owner" && !(Array.isArray(r.recipients) && r.recipients.length));
      if (bad) {
        this.setState({
          notice: {
            tone: "warning",
            text: "The owner digest needs at least one recipient before it is switched on."
          }
        });
        return;
      }
      this.setState({
        busy: true,
        notice: null
      });
      try {
        // Retail has no reps, dealers, territories or quotes desk: those owner sections stay off.
        const settings = this.state.bm === "retail" ? data.settings.map(r => r.kind === "owner" ? {
          ...r,
          sections: {
            ...r.sections,
            ...Object.fromEntries(RETAIL_HIDDEN_OWNER.map(k => [k, false]))
          }
        } : r) : data.settings;
        await putDigestSettings(data.timezone, settings);
        const fresh = await getDigestSettings();
        if (this._alive) this.setState({
          busy: false,
          dirty: false,
          data: fresh || data,
          notice: {
            tone: "info",
            text: "Digest settings saved."
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          notice: {
            tone: "danger",
            text: errText(e, "The settings could not be saved.")
          }
        });
      }
    });
    _defineProperty(this, "sendTest", async () => {
      const {
        testKind,
        testEmail,
        testScope
      } = this.state;
      if (!emailOk(testEmail)) {
        this.setState({
          notice: {
            tone: "warning",
            text: "Enter the email the test should go to."
          }
        });
        return;
      }
      this.setState({
        busy: true,
        testResult: null
      });
      try {
        const r = await sendDigestNow(testKind, {
          test: true,
          email: testEmail.trim(),
          scope: testScope || undefined
        });
        if (!this._alive) return;
        // The test renders synchronously: { ok, subject, scope, periodEnd, error, text, facts }. Keep the modal open with the preview.
        this.setState({
          busy: false,
          testResult: r,
          notice: r.error ? {
            tone: "warning",
            text: `The test rendered with a problem: ${r.error}`
          } : {
            tone: "info",
            text: `Test ${testKind} digest sent to ${testEmail.trim()}.`
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          notice: {
            tone: "danger",
            text: errText(e, "The test could not be sent.")
          }
        });
      }
    });
    _defineProperty(this, "runNow", async () => {
      const {
        runKind
      } = this.state;
      this.setState({
        busy: true
      });
      try {
        const r = await sendDigestNow(runKind, {
          test: false
        });
        if (!this._alive) return;
        toast("Queued, sends within 5 minutes");
        this.setState({
          busy: false,
          modal: null,
          notice: {
            tone: "info",
            text: `${runKind} digest queued for this workspace${r.eta ? ` (${r.eta})` : ""}; it sends within 5 minutes and lands in delivery history as it finishes.`
          }
        });
        setTimeout(() => this._alive && this.loadRuns(), 15000);
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          notice: {
            tone: "danger",
            text: errText(e, "The run could not be started.")
          }
        });
      }
    });
    _defineProperty(this, "removeOptOut", async o => {
      const r = await removeDigestOptOut(o).catch(() => null);
      if (!this._alive) return;
      if (r === null) this.setState({
        notice: {
          tone: "info",
          text: "Opt-outs are removed by the recipient's own link for now."
        }
      });else this.setState({
        optOuts: (this.state.optOuts || []).filter(x => x !== o)
      });
    });
    _defineProperty(this, "addOptOut", async () => {
      const {
        optEmail,
        optKind
      } = this.state;
      if (!emailOk(optEmail)) {
        this.setState({
          notice: {
            tone: "warning",
            text: "Enter the email to opt out."
          }
        });
        return;
      }
      this.setState({
        optBusy: true
      });
      try {
        await addDigestOptOut(optEmail.trim().toLowerCase(), optKind);
        const fresh = await listDigestOptOuts();
        if (this._alive) this.setState({
          optBusy: false,
          optEmail: "",
          optOuts: fresh || (this.state.optOuts || []).concat([{
            email: optEmail.trim().toLowerCase(),
            kind: optKind,
            createdDate: new Date().toISOString()
          }])
        });
      } catch (e) {
        if (this._alive) this.setState({
          optBusy: false,
          notice: {
            tone: "danger",
            text: errText(e, "The opt-out could not be added.")
          }
        });
      }
    });
    this.state = {
      data: undefined,
      error: false,
      dirty: false,
      busy: false,
      notice: null,
      bm: null,
      cadence: {
        owner: "weekly",
        rep: "weekly",
        store: "weekly"
      },
      reps: null,
      stores: null,
      runs: undefined,
      runSel: null,
      runBusy: false,
      optOuts: undefined,
      draft: {},
      open: {},
      modal: null,
      testKind: "",
      testEmail: currentUserEmail(),
      testScope: "",
      runKind: "",
      testResult: null,
      optEmail: "",
      optKind: "all",
      optBusy: false
    };
    this._alive = true;
  }
  componentDidMount() {
    resolveBusinessModel().then(bm => this._alive && this.setState({
      bm
    })).catch(() => this._alive && this.setState({
      bm: "both"
    }));
    getDigestSettings().then(d => this._alive && this.setState({
      data: d,
      cadence: initialCadence(d)
    })).catch(() => this._alive && this.setState({
      error: true
    }));
    listReps().then(r => this._alive && this.setState({
      reps: r
    })).catch(() => this._alive && this.setState({
      reps: null
    }));
    listStores().then(s => this._alive && this.setState({
      stores: s
    })).catch(() => this._alive && this.setState({
      stores: null
    }));
    this.loadRuns();
    listDigestOptOuts().then(o => this._alive && this.setState({
      optOuts: o
    })).catch(() => this._alive && this.setState({
      optOuts: null
    }));
  }
  componentWillUnmount() {
    this._alive = false;
  }
  loadRuns() {
    listDigestRuns("", 30).then(r => this._alive && this.setState({
      runs: r
    })).catch(() => this._alive && this.setState({
      runs: null
    }));
  }
  row(kind) {
    const cad = this.state.cadence[kind];
    return (this.state.data.settings || []).find(s => s.kind === kind && s.cadence === cad);
  }
  edit(kind, patch) {
    const cad = this.state.cadence[kind];
    this.setState(s => ({
      data: {
        ...s.data,
        settings: s.data.settings.map(r => r.kind === kind && r.cadence === cad ? {
          ...r,
          ...patch
        } : r)
      },
      dirty: true
    }));
  }
  openRun(r) {
    this.setState({
      runSel: r,
      runBusy: true
    });
    getDigestRun(r.id).then(full => this._alive && this.setState({
      runSel: full || r,
      runBusy: false
    })).catch(() => this._alive && this.setState({
      runBusy: false
    }));
  }
  // ---- card pieces ------------------------------------------------------------------
  chips(list, onChange, placeholder) {
    return /*#__PURE__*/React.createElement("span", {
      className: "dd-chips"
    }, list.map(e => /*#__PURE__*/React.createElement("span", {
      key: e,
      className: "dd-chip"
    }, e, /*#__PURE__*/React.createElement("a", {
      onClick: () => onChange(list.filter(x => x !== e)),
      title: "Remove",
      "aria-label": `Remove ${e}`
    }, "\u2715"))), list.length < 10 ? /*#__PURE__*/React.createElement(ChipInput, {
      list: list,
      onChange: onChange,
      placeholder: placeholder || "Add an email"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "10 at most"));
  }
  /** Rep emails missing from the rep feed: { noEmail, total } or nulls when unknown. */
  repEmailGap() {
    const {
      reps,
      data
    } = this.state;
    const noEmail = data.repsWithoutEmail != null ? data.repsWithoutEmail : reps ? reps.filter(x => x.status !== "terminated" && !x.email).length : null;
    const total = data.repsTotal != null ? data.repsTotal : reps ? reps.filter(x => x.status !== "terminated").length : null;
    return {
      noEmail,
      total
    };
  }
  who(kind, r) {
    if (kind === "owner") {
      const rec = Array.isArray(r.recipients) ? r.recipients : [];
      return rec.length ? listWords(rec) : "no one yet";
    }
    if (kind === "rep") {
      const all = r.recipients === null || r.recipients === undefined || Array.isArray(r.recipients) && r.recipients.length === 0 && r._mode !== "picked";
      if (all) return "every rep with an email address";
      const n = Array.isArray(r.recipients) ? r.recipients.length : 0;
      return n ? `${n} picked rep${n === 1 ? "" : "s"}` : "no reps picked yet";
    }
    const {
      stores,
      data
    } = this.state;
    const list = (data.stores && data.stores.length ? data.stores : stores) || [];
    const rec = r.recipients && typeof r.recipients === "object" && !Array.isArray(r.recipients) ? r.recipients : {};
    const n = list.filter(s => Array.isArray(rec[s.storeId]) ? rec[s.storeId].length : !!s.managerEmail).length;
    return n ? `the managers of ${n} store${n === 1 ? "" : "s"}` : "no store managers yet";
  }
  summary(kind, r) {
    const tz = this.state.data.timezone || "America/New_York";
    const at = r.cadence === "monthly" ? `on the first business day of each month at ${hourLabel(r.hourLocal)}` : `every ${DAY_NAME[String(r.day)] || "Monday"} at ${hourLabel(r.hourLocal)}`;
    if (!r.enabled) return `Off · ${KIND_ABOUT[kind] || ""} ${at}${KIND_ABOUT_TAIL[kind] || ""}`;
    const cover = r.cadence === "monthly" ? "last month" : r.periodEnd === "send_day" ? "the 7 days before it sends" : "last week";
    return `${cap(at)} ${tzCity(tz)}, covering ${cover}, to ${this.who(kind, r)}`;
  }
  toggle(kind, r) {
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `dd-tog${r.enabled ? " on" : ""}`,
      role: "switch",
      "aria-checked": !!r.enabled,
      "aria-label": r.enabled ? "Turn off" : "Turn on",
      onClick: () => this.edit(kind, {
        enabled: !r.enabled
      })
    });
  }
  whoField(kind, r) {
    const {
      reps,
      stores,
      data
    } = this.state;
    if (kind === "owner") {
      const rec = Array.isArray(r.recipients) ? r.recipients : [];
      return /*#__PURE__*/React.createElement("label", {
        className: "dd-field c3"
      }, "Who gets it", /*#__PURE__*/React.createElement("span", {
        className: "dd-box"
      }, this.chips(rec, v => this.edit("owner", {
        recipients: v
      }))));
    }
    if (kind === "rep") {
      const all = r.recipients === null || r.recipients === undefined || Array.isArray(r.recipients) && r.recipients.length === 0 && r._mode !== "picked";
      const picked = Array.isArray(r.recipients) ? r.recipients : [];
      return /*#__PURE__*/React.createElement("div", {
        className: "dd-field c3"
      }, /*#__PURE__*/React.createElement("span", {
        className: "l"
      }, "Who gets it"), /*#__PURE__*/React.createElement("span", {
        className: "dd-box"
      }, /*#__PURE__*/React.createElement("select", {
        className: "dd-inline",
        value: all ? "all" : "picked",
        onChange: e => this.edit("rep", e.target.value === "all" ? {
          recipients: null,
          _mode: "all"
        } : {
          recipients: [],
          _mode: "picked"
        })
      }, /*#__PURE__*/React.createElement("option", {
        value: "all"
      }, "Every rep with an email address"), /*#__PURE__*/React.createElement("option", {
        value: "picked"
      }, "Only the reps I pick")), !all ? /*#__PURE__*/React.createElement("span", {
        className: "dd-chips"
      }, picked.map(code => {
        const rep = (reps || []).find(x => x.repCode === code);
        return /*#__PURE__*/React.createElement("span", {
          key: code,
          className: "dd-chip"
        }, rep ? rep.name : code, rep && !rep.email ? " (no email)" : "", /*#__PURE__*/React.createElement("a", {
          onClick: () => this.edit("rep", {
            recipients: picked.filter(x => x !== code),
            _mode: "picked"
          }),
          title: "Remove"
        }, "\u2715"));
      }), /*#__PURE__*/React.createElement("select", {
        className: "dd-chip-in",
        value: "",
        onChange: e => {
          if (e.target.value && !picked.includes(e.target.value)) this.edit("rep", {
            recipients: picked.concat([e.target.value]),
            _mode: "picked"
          });
        }
      }, /*#__PURE__*/React.createElement("option", {
        value: ""
      }, "Add a rep"), (reps || []).filter(x => x.status !== "terminated" && !picked.includes(x.repCode)).map(x => /*#__PURE__*/React.createElement("option", {
        key: x.repCode,
        value: x.repCode
      }, x.name, " \xB7 ", x.repCode, x.email ? "" : " (no email)")))) : null));
    }
    const list = (data.stores && data.stores.length ? data.stores : stores) || [];
    const rec = r.recipients && typeof r.recipients === "object" && !Array.isArray(r.recipients) ? r.recipients : {};
    return /*#__PURE__*/React.createElement("div", {
      className: "dd-field c4"
    }, /*#__PURE__*/React.createElement("span", {
      className: "l"
    }, "Who gets it"), /*#__PURE__*/React.createElement("div", {
      className: "dd-stores"
    }, list.length === 0 ? /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "No stores in your store file yet.") : list.map(s => {
      const emails = Array.isArray(rec[s.storeId]) ? rec[s.storeId] : s.managerEmail ? [s.managerEmail] : [];
      return /*#__PURE__*/React.createElement("div", {
        className: "dd-store",
        key: s.storeId
      }, /*#__PURE__*/React.createElement("b", null, s.name), /*#__PURE__*/React.createElement("span", {
        className: "dd-box"
      }, this.chips(emails, v => this.edit("store", {
        recipients: {
          ...rec,
          [s.storeId]: v
        }
      }), s.managerEmail || "Manager's email")));
    })));
  }
  lastRunLine(kind, r) {
    const run = (this.state.runs || []).find(x => x.kind === kind && x.cadence === r.cadence && x.status !== "test");
    return /*#__PURE__*/React.createElement("span", {
      className: "dd-last"
    }, run ? /*#__PURE__*/React.createElement(React.Fragment, null, "Last run ", fmtDateTime(run.occurrence || run.startedDate), ": ", runWords(run), ". ", /*#__PURE__*/React.createElement("a", {
      onClick: () => this.openRun(run)
    }, "See what was sent")) : r.lastSentDate ? /*#__PURE__*/React.createElement(React.Fragment, null, "Last sent ", fmtDateTime(r.lastSentDate), ".") : "Not sent yet.", r.enabled && r.nextDueUtc ? /*#__PURE__*/React.createElement(React.Fragment, null, " Next one ", fmtDateTime(r.nextDueUtc), ".") : null, r.optedOut ? /*#__PURE__*/React.createElement(React.Fragment, null, " ", r.optedOut, " ", r.optedOut === 1 ? "person has" : "people have", " unsubscribed.") : null);
  }
  settings(kind, r, admin) {
    const {
      data,
      busy,
      dirty,
      bm
    } = this.state;
    const tz = data.timezone || "America/New_York";
    const cad = r.cadence;
    const testScope = kind === "rep" ? ((this.state.reps || []).find(x => x.email) || {}).repCode : "";
    const secs = (DIGEST_SECTIONS[kind] || []).filter(([k]) => !(kind === "owner" && bm === "retail" && RETAIL_HIDDEN_OWNER.includes(k)));
    return /*#__PURE__*/React.createElement("div", {
      className: "dd-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: admin ? "" : "dd-ro"
    }, /*#__PURE__*/React.createElement("div", {
      className: "dd-grid"
    }, /*#__PURE__*/React.createElement("label", {
      className: "dd-field"
    }, "How often", /*#__PURE__*/React.createElement("select", {
      value: cad,
      onChange: e => {
        const c = e.target.value;
        this.setState(s => ({
          cadence: {
            ...s.cadence,
            [kind]: c
          }
        }));
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: "weekly"
    }, "Every week"), /*#__PURE__*/React.createElement("option", {
      value: "monthly"
    }, "Every month"))), /*#__PURE__*/React.createElement("label", {
      className: "dd-field"
    }, "On", cad === "weekly" ? /*#__PURE__*/React.createElement("select", {
      value: String(r.day),
      onChange: e => this.edit(kind, {
        day: Number(e.target.value)
      })
    }, DAYS.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
      key: v,
      value: v
    }, l))) : /*#__PURE__*/React.createElement("select", {
      value: String(r.day),
      onChange: e => this.edit(kind, {
        day: Number(e.target.value)
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: "1"
    }, "The first business day"))), /*#__PURE__*/React.createElement("label", {
      className: "dd-field"
    }, "At", /*#__PURE__*/React.createElement("select", {
      value: String(r.hourLocal),
      onChange: e => this.edit(kind, {
        hourLocal: Number(e.target.value)
      })
    }, HOURS.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
      key: v,
      value: v
    }, l)))), /*#__PURE__*/React.createElement("label", {
      className: "dd-field",
      title: "Every digest uses the same time zone"
    }, "Time zone", /*#__PURE__*/React.createElement("select", {
      value: tz,
      onChange: e => this.setState({
        data: {
          ...data,
          timezone: e.target.value
        },
        dirty: true
      })
    }, ZONES.concat(ZONES.includes(tz) ? [] : [tz]).map(z => /*#__PURE__*/React.createElement("option", {
      key: z,
      value: z
    }, tzCity(z))))), this.whoField(kind, r), /*#__PURE__*/React.createElement("label", {
      className: `dd-field${kind === "store" ? " c2" : ""}`
    }, "Replies go to", /*#__PURE__*/React.createElement("input", {
      value: r.replyTo || "",
      placeholder: kind === "owner" ? "Owner's email" : kind === "rep" ? "Sales lead or owner" : "Email for replies",
      onChange: e => this.edit(kind, {
        replyTo: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      className: "dd-field"
    }, "Covering", cad === "weekly" ? /*#__PURE__*/React.createElement("select", {
      value: r.periodEnd,
      onChange: e => this.edit(kind, {
        periodEnd: e.target.value
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: "previous_sunday"
    }, "Last week, Monday to Sunday"), /*#__PURE__*/React.createElement("option", {
      value: "send_day"
    }, "The 7 days before it sends")) : /*#__PURE__*/React.createElement("span", {
      className: "dd-static"
    }, "Last month, next to the same month last year")), /*#__PURE__*/React.createElement("label", {
      className: "dd-field"
    }, "Which orders count", /*#__PURE__*/React.createElement("select", {
      value: r.basis,
      onChange: e => this.edit(kind, {
        basis: e.target.value
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: "booked"
    }, "Every booked order"), /*#__PURE__*/React.createElement("option", {
      value: "invoiced"
    }, "Only invoiced orders"))), /*#__PURE__*/React.createElement("p", {
      className: "dd-hint c2"
    }, "Booked counts an order when it is placed. Invoiced waits until it is billed.")), /*#__PURE__*/React.createElement("div", {
      className: "dd-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "l"
    }, "What's in it"), /*#__PURE__*/React.createElement("div", {
      className: "dd-pills"
    }, secs.map(([k, l]) => {
      const on = r.sections[k] !== false;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: k,
        className: `dd-pill${on ? " on" : ""}`,
        "aria-pressed": on,
        onClick: () => this.edit(kind, {
          sections: {
            ...r.sections,
            [k]: !on
          }
        })
      }, on ? "✓ " : "", (SECTION_LABELS[kind] || {})[k] || l);
    })), kind === "rep" ? /*#__PURE__*/React.createElement("span", {
      className: "dd-hint"
    }, "Dealers who become at risk during a run also start the win-back ", /*#__PURE__*/React.createElement(Link, {
      to: "/Email/journeys"
    }, "email journey"), ".") : null)), /*#__PURE__*/React.createElement("div", {
      className: "dd-foot"
    }, this.lastRunLine(kind, r), admin ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => this.setState({
        modal: "test",
        testKind: kind,
        testEmail: currentUserEmail(),
        testScope: testScope || ""
      })
    }, "Send me a test"), /*#__PURE__*/React.createElement(Btn, {
      small: true,
      disabled: !r.enabled,
      title: r.enabled ? "Send this digest to everyone on it now" : "Turn the digest on and save first",
      onClick: () => this.setState({
        modal: "run",
        runKind: kind
      })
    }, "Send it now"), /*#__PURE__*/React.createElement(Btn, {
      small: true,
      primary: true,
      disabled: busy || !dirty,
      onClick: this.save
    }, busy ? "Saving…" : "Save changes")) : null));
  }
  digestCard(kind, label) {
    const r = this.row(kind);
    if (!r) return null;
    const admin = isAdmin() && this.state.data.canEdit !== false;
    const open = !!this.state.open[kind];
    const otherCad = r.cadence === "weekly" ? "monthly" : "weekly";
    const other = (this.state.data.settings || []).find(s => s.kind === kind && s.cadence === otherCad);
    const testScope = kind === "rep" ? ((this.state.reps || []).find(x => x.email) || {}).repCode : "";
    if (kind === "store" && (this.state.bm === "manufacturing" || this.state.stores === null && !(this.state.data.stores || []).length)) {
      return /*#__PURE__*/React.createElement("section", {
        className: "dd-card",
        key: kind
      }, /*#__PURE__*/React.createElement("div", {
        className: "dd-head"
      }, /*#__PURE__*/React.createElement("span", {
        className: "dd-tog off-na",
        "aria-hidden": "true"
      }), /*#__PURE__*/React.createElement("div", {
        className: "dd-title"
      }, /*#__PURE__*/React.createElement("b", null, label), /*#__PURE__*/React.createElement("span", null, "Not used here: this workspace has no stores. On a workspace with stores, each store manager gets their own store."))));
    }
    return /*#__PURE__*/React.createElement("section", {
      className: `dd-card${open ? " open" : ""}`,
      key: kind
    }, /*#__PURE__*/React.createElement("div", {
      className: "dd-head"
    }, admin ? this.toggle(kind, r) : /*#__PURE__*/React.createElement("span", {
      className: `dd-tog ro${r.enabled ? " on" : ""}`,
      "aria-label": r.enabled ? "On" : "Off"
    }), /*#__PURE__*/React.createElement("div", {
      className: "dd-title"
    }, /*#__PURE__*/React.createElement("b", null, label), /*#__PURE__*/React.createElement("span", null, this.summary(kind, r)), other && other.enabled ? /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "The ", otherCad, " edition is on too.") : null), admin && r.enabled ? /*#__PURE__*/React.createElement(Btn, {
      onClick: () => this.setState({
        modal: "test",
        testKind: kind,
        testEmail: currentUserEmail(),
        testScope: testScope || ""
      })
    }, "Send me a test") : null, /*#__PURE__*/React.createElement(Btn, {
      onClick: () => this.setState(s => ({
        open: {
          ...s.open,
          [kind]: !open
        }
      })),
      "aria-expanded": open
    }, open ? "Close" : admin ? "Change" : "Details")), open ? this.settings(kind, r, admin) : null);
  }
  renderRun(run) {
    const facts = run.facts && typeof run.facts === "object" ? run.facts : null;
    const factRows = facts ? Object.entries(facts).filter(([, v]) => v && typeof v === "object" && ("value" in v || "Value" in v)).map(([k, v]) => ({
      k,
      value: v.value != null ? v.value : v.Value,
      prior: v.prior != null ? v.prior : v.Prior,
      basis: v.basis || v.Basis,
      word: v.word || v.Word,
      delta: v.delta != null ? v.delta : v.Delta
    })) : [];
    return /*#__PURE__*/React.createElement(Modal, {
      title: `${run.kind} digest · ${run.cadence} · period ending ${fmtDate(run.periodEnd)}`,
      width: 760,
      onClose: () => this.setState({
        runSel: null
      }),
      footer: /*#__PURE__*/React.createElement(Btn, {
        onClick: () => this.setState({
          runSel: null
        })
      }, "Close"),
      note: `${run.status} · sent ${run.sent} of ${run.recipients}${run.bandChanges ? ` · ${run.bandChanges} band changes` : ""}${run.error ? ` · ${run.error}` : ""}`
    }, this.state.runBusy ? /*#__PURE__*/React.createElement("p", {
      className: "ec-help"
    }, "Loading\u2026") : null, factRows.length ? /*#__PURE__*/React.createElement("div", {
      className: "tblwrap",
      style: {
        maxHeight: 260
      }
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Fact"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Value"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Prior"), /*#__PURE__*/React.createElement("th", null, "Comparison"))), /*#__PURE__*/React.createElement("tbody", null, factRows.map(f => /*#__PURE__*/React.createElement("tr", {
      key: f.k
    }, /*#__PURE__*/React.createElement("td", null, f.k.replace(/([A-Z])/g, " $1").replace(/_/g, " ").toLowerCase()), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, typeof f.value === "number" ? f.value.toLocaleString("en-US") : String(f.value)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, f.prior != null ? typeof f.prior === "number" ? f.prior.toLocaleString("en-US") : String(f.prior) : "·"), /*#__PURE__*/React.createElement("td", null, [f.word, f.delta != null ? `${f.delta > 0 ? "+" : ""}${Math.round(f.delta * 10) / 10}%` : null, f.basis ? String(f.basis).replace(/_/g, " ") : null].filter(Boolean).join(" · ") || "no comparison")))))) : facts ? /*#__PURE__*/React.createElement("pre", {
      className: "dg-facts"
    }, JSON.stringify(facts, null, 2).slice(0, 4000)) : /*#__PURE__*/React.createElement("p", {
      className: "ec-help"
    }, "No facts stored for this run."), /*#__PURE__*/React.createElement("h4", {
      className: "dg-h4"
    }, "Deliveries"), run.deliveries.length === 0 ? /*#__PURE__*/React.createElement("p", {
      className: "ec-help"
    }, "No deliveries recorded.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap",
      style: {
        maxHeight: 240
      }
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Scope"), /*#__PURE__*/React.createElement("th", null, "Recipient"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Note"))), /*#__PURE__*/React.createElement("tbody", null, run.deliveries.map(d => /*#__PURE__*/React.createElement("tr", {
      key: d.id
    }, /*#__PURE__*/React.createElement("td", null, d.scopeKind, d.scopeId ? ` · ${d.scopeId}` : ""), /*#__PURE__*/React.createElement("td", null, d.email || (d.emailHash ? `${d.emailHash.slice(0, 10)}…` : "·")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
      tone: {
        sent: "success",
        queued: "info",
        bounced: "danger",
        complained: "danger",
        skipped: "warning"
      }[d.status] || "neutral"
    }, d.status)), /*#__PURE__*/React.createElement("td", {
      className: "dim"
    }, d.reason)))))), run.skipped.length ? /*#__PURE__*/React.createElement("p", {
      className: "ec-help"
    }, "Skipped: ", run.skipped.map(s => `${s.scope}${s.reason ? ` (${s.reason})` : ""}`).join(", ")) : null);
  }
  render() {
    const {
      data,
      error,
      dirty,
      busy,
      notice,
      runs,
      runSel,
      optOuts,
      modal,
      testKind,
      testEmail,
      testScope,
      runKind,
      reps,
      bm
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    const admin = isAdmin() && (data && data.canEdit === false ? false : true);
    if (!isAdmin() && !isSalesUser()) return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Digests",
      subtitle: "Sales digests"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, "Sales digests are managed by owners and administrators."));
    const retail = bm === "retail";
    // Rep digest only where there is a rep roster (manufacturers, or "both" with reps); store digest only where there are stores (retail, or "both" with a store roster).
    const hasReps = !!(this.state.reps && this.state.reps.length);
    const hasStores = !!(this.state.stores && this.state.stores.length);
    const kinds = DIGEST_KINDS.filter(([k]) => {
      if (k === "rep") return bm === "manufacturing" || bm === "both" && hasReps || !bm && !retail;
      if (k === "store") return bm === "retail" || bm === "both" && hasStores;
      return true;
    });
    const gap = data ? this.repEmailGap() : {
      noEmail: null,
      total: null
    };
    const showGap = !!data && kinds.some(([k]) => k === "rep") && gap.noEmail > 0 && gap.total > 0;
    const kindLabel = k => (DIGEST_KINDS.find(([x]) => x === k) || [k, `${k} digest`])[1];
    return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Digests",
      subtitle: "Weekly or monthly sales emails for owners, reps and stores",
      cls: "wa-editor dg-view dd-view",
      chips: data ? [{
        k: "On",
        v: data.settings.filter(s => s.enabled).length
      }, optOuts && optOuts.length ? {
        k: "Unsubscribed",
        v: optOuts.length
      } : typeof data.optOuts === "number" && data.optOuts ? {
        k: "Unsubscribed",
        v: data.optOuts
      } : null] : [],
      actions: admin && data ? /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        disabled: busy || !dirty,
        onClick: this.save
      }, busy ? "Saving…" : dirty ? "Save changes" : "Saved") : null
    }, notice ? /*#__PURE__*/React.createElement(Notice, {
      tone: notice.tone,
      onClose: () => this.setState({
        notice: null
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, notice.text)) : null, data === undefined ? /*#__PURE__*/React.createElement(Loader, null) : data === null ? /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Sales digests"
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Sales digests aren't turned on for this workspace yet. Your settings will show up here once they are."))) : /*#__PURE__*/React.createElement("div", {
      className: "dd-stack"
    }, showGap ? /*#__PURE__*/React.createElement("section", {
      className: "dd-banner",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dot",
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("div", {
      className: "t"
    }, /*#__PURE__*/React.createElement("b", null, gap.noEmail >= gap.total ? `None of your ${gap.total} reps has an email address yet` : `${gap.noEmail} of your ${gap.total} reps ${gap.noEmail === 1 ? "has" : "have"} no email address`), /*#__PURE__*/React.createElement("span", null, "The rep digest can't reach ", gap.noEmail >= gap.total ? "them" : "those reps", " until rep emails are in your sales rep file. Add an email column to that file and send it again.")), /*#__PURE__*/React.createElement(Link, {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      to: "/Data"
    }, "Add rep emails")) : null, kinds.map(([kind, label]) => this.digestCard(kind, label)), /*#__PURE__*/React.createElement("div", {
      className: "dd-bottom"
    }, /*#__PURE__*/React.createElement("section", {
      className: "dd-card dd-list"
    }, /*#__PURE__*/React.createElement("b", {
      className: "h"
    }, "Sent"), runs === undefined ? /*#__PURE__*/React.createElement(Loader, null) : runs === null ? /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "The send history isn't available yet.") : runs.length === 0 ? /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "Nothing sent yet. Send yourself a test to see one.") : /*#__PURE__*/React.createElement("div", {
      className: "dd-lines"
    }, runs.map(r => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: r.id,
      className: "dd-line",
      onClick: () => this.openRun(r)
    }, /*#__PURE__*/React.createElement("span", {
      className: `dot ${runTone(r.status)}`,
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("span", {
      className: "w"
    }, kindLabel(r.kind), r.cadence === "monthly" ? " (monthly)" : "", ", ", runWords(r)), /*#__PURE__*/React.createElement("span", {
      className: "d"
    }, relTime(r.startedDate || r.occurrence)))))), /*#__PURE__*/React.createElement("section", {
      className: "dd-card dd-list"
    }, /*#__PURE__*/React.createElement("b", {
      className: "h"
    }, "Unsubscribed"), optOuts === undefined ? /*#__PURE__*/React.createElement(Loader, null) : optOuts === null ? /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, typeof data.optOuts === "number" && data.optOuts ? `${data.optOuts} ${data.optOuts === 1 ? "person has" : "people have"} unsubscribed.` : "The list isn't available yet. Anyone who unsubscribes is always skipped.") : optOuts.length === 0 ? /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "No one has unsubscribed.") : /*#__PURE__*/React.createElement("div", {
      className: "dd-lines"
    }, optOuts.map((o, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "dd-line static"
    }, /*#__PURE__*/React.createElement("span", {
      className: "w"
    }, o.email || (o.emailHash ? `${o.emailHash.slice(0, 10)}…` : "Unknown address"), " ", /*#__PURE__*/React.createElement("span", {
      className: "dd-muted"
    }, "from ", o.kind === "all" ? "every digest" : `the ${kindLabel(o.kind).toLowerCase()}`, o.createdDate ? `, ${fmtDate(o.createdDate)}` : "")), admin ? /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink muted",
      onClick: () => this.removeOptOut(o)
    }, "Remove") : null))), admin && optOuts !== null ? /*#__PURE__*/React.createElement("div", {
      className: "dd-optadd"
    }, /*#__PURE__*/React.createElement("input", {
      placeholder: "Unsubscribe an email",
      value: this.state.optEmail,
      onChange: e => this.setState({
        optEmail: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.addOptOut();
      }
    }), /*#__PURE__*/React.createElement("select", {
      value: this.state.optKind,
      onChange: e => this.setState({
        optKind: e.target.value
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: "all"
    }, "from every digest"), DIGEST_KINDS.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, "from the ", l.toLowerCase()))), /*#__PURE__*/React.createElement(Btn, {
      small: true,
      disabled: this.state.optBusy,
      onClick: this.addOptOut
    }, this.state.optBusy ? "Adding…" : "Add")) : null))), runSel ? this.renderRun(runSel) : null, modal === "test" ? /*#__PURE__*/React.createElement(Modal, {
      title: `Send a test ${testKind} digest`,
      width: this.state.testResult ? 640 : 460,
      onClose: () => this.setState({
        modal: null,
        testResult: null
      }),
      busy: busy,
      footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Btn, {
        onClick: () => this.setState({
          modal: null,
          testResult: null
        })
      }, this.state.testResult ? "Close" : "Cancel"), /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        disabled: busy,
        onClick: this.sendTest
      }, busy ? "Sending…" : this.state.testResult ? "Send again" : "Send test")),
      note: "Builds the digest from the latest numbers and emails only you."
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "To"), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: testEmail,
      onChange: e => this.setState({
        testEmail: e.target.value
      })
    })), testKind === "rep" ? /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Which rep's book"), /*#__PURE__*/React.createElement("select", {
      className: "ec-input",
      value: testScope,
      onChange: e => this.setState({
        testScope: e.target.value
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "choose a rep"), (reps || []).map(x => /*#__PURE__*/React.createElement("option", {
      key: x.repCode,
      value: x.repCode
    }, x.name, " \xB7 ", x.repCode)))) : null, testKind === "store" ? /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Which store"), /*#__PURE__*/React.createElement("select", {
      className: "ec-input",
      value: testScope,
      onChange: e => this.setState({
        testScope: e.target.value
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "choose a store"), ((data.stores && data.stores.length ? data.stores : this.state.stores) || []).map(x => /*#__PURE__*/React.createElement("option", {
      key: x.storeId,
      value: x.storeId
    }, x.name)))) : null, this.state.testResult ? /*#__PURE__*/React.createElement("div", {
      className: "dg-preview"
    }, /*#__PURE__*/React.createElement("b", null, this.state.testResult.subject || "(no subject)"), /*#__PURE__*/React.createElement("span", {
      className: "ec-help"
    }, [this.state.testResult.periodEnd ? `period ending ${fmtDate(this.state.testResult.periodEnd)}` : null, this.state.testResult.scope ? `scope ${this.state.testResult.scope}` : null, this.state.testResult.error ? `problem: ${this.state.testResult.error}` : null].filter(Boolean).join(" · ")), this.state.testResult.text ? /*#__PURE__*/React.createElement("pre", {
      className: "dg-facts"
    }, String(this.state.testResult.text).slice(0, 6000)) : /*#__PURE__*/React.createElement("span", {
      className: "ec-help"
    }, "No text part came back.")) : null) : null, modal === "run" ? /*#__PURE__*/React.createElement(Modal, {
      title: `Send the ${runKind} digest now`,
      width: 460,
      onClose: () => this.setState({
        modal: null
      }),
      busy: busy,
      footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Btn, {
        onClick: () => this.setState({
          modal: null
        })
      }, "Cancel"), /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        disabled: busy,
        onClick: this.runNow
      }, busy ? "Starting…" : "Send it now"))
    }, /*#__PURE__*/React.createElement("p", {
      className: "ec-help"
    }, "Sends this digest for the latest period to everyone on it, just like a scheduled send. People who unsubscribed are skipped. Send yourself a test first if you want to look.")) : null);
  }
}
export default withRouter(SalesDigestsView);